import { Control, DateRange, MultiSelect } from "@/@core/components/alt-ui/controls";
import { Footer } from "@/@core/controls/footer.control";
import { FilterController } from "@/@core/filters/filter-controller";
import { FilterType, FilterPair } from "@/utils/filter";
import { IEmployee, ISaleStage, IShop, ITableFilter, IUser } from "@lib";

export type SalesFilter = Partial<{
    shop: string[];
    createdAt: FilterPair;
    doneAt: FilterPair;
    seller: string[];
    stage: string[];
}>;

export interface ISalesFilterContext {
    shops: IShop[];
    stages: ISaleStage[];
    employees: IEmployee[];
    defaultFilter: SalesFilter;
    currentFilter: ITableFilter[];
}

export class SalesFilterController extends FilterController<ISalesFilterContext, void> {
    public id: string = "sales-filter";
    public title: string = "Фильтр";

    public filter = {
        shop: {
            control: new MultiSelect<IShop>(),

            type: FilterType.In,

            get: (): string[] => {
                return this.filter.shop.control.selectedItems.map(shop => shop.id);
            },
            set: (ids: string[]): void => {
                this.filter.shop.control.selectedItems = this.findItemsByIds(this.context.shops, ids);
            },
        },
        createdAt: {
            control: new DateRange(),

            type: FilterType.Between,

            get: (): FilterPair => {
                return this.filter.createdAt.control.value;
            },
            set: (date: FilterPair): void => {
                this.filter.createdAt.control.value = date;
            },
        },
        doneAt: {
            control: new DateRange(),

            type: FilterType.Between,

            get: (): FilterPair => {
                return this.filter.doneAt.control.value;
            },
            set: (date: FilterPair): void => {
                this.filter.doneAt.control.value = date;
            },
        },
        stage: {
            control: new MultiSelect<ISaleStage>(),

            type: FilterType.In,

            get: (): string[] => {
                return this.filter.stage.control.selectedItems.map(stage => stage.id);
            },
            set: (ids: string[]): void => {
                this.filter.stage.control.selectedItems = this.findItemsByIds(this.context.stages, ids);
            },
        },
        seller: {
            control: new MultiSelect<IUser>(),

            type: FilterType.In,

            get: (): string[] => {
                return this.filter.seller.control.selectedItems.map(e => e.id);
            },
            set: (ids: string[]): void => {
                this.filter.seller.control.selectedItems = this.findItemsByIds(
                    this.context.employees.map(e => e.userRef),
                    ids,
                );
            },
        },
    };

    private ftFooter!: Footer;

    private context!: ISalesFilterContext;

    public onSave: ((filter: ITableFilter[]) => Promise<boolean>) | null = null;

    public init(context: ISalesFilterContext): void {
        this.context = context;

        this.initControls();
        this.initFooter();
        this.populateFilters();
    }

    public show(context: ISalesFilterContext): Promise<void> {
        this.context = context;
        this.initControls();
        this.initFooter();
        this.populateFilters();

        return super.show(context);
    }

    // Init

    private initControls(): void {
        this.filter.shop.control = new MultiSelect<IShop>();
        this.filter.shop.control.id = this.getControlId("shop");
        this.filter.shop.control.label = "Магазин";
        this.filter.shop.control.items = this.context?.shops ?? [];
        this.filter.shop.control.textField = (shop: IShop) => shop.info.name;

        this.filter.createdAt.control = new DateRange();
        this.filter.createdAt.control.id = this.getControlId("createdAt");
        this.filter.createdAt.control.label = "Дата создания";

        this.filter.doneAt.control = new DateRange();
        this.filter.doneAt.control.id = this.getControlId("doneAt");
        this.filter.doneAt.control.label = "Дата завершения";

        this.filter.stage.control = new MultiSelect<ISaleStage>();
        this.filter.stage.control.id = this.getControlId("stage");
        this.filter.stage.control.label = "Этап";
        this.filter.stage.control.items = this.context?.stages ?? [];
        this.filter.stage.control.textField = (stage: ISaleStage) => stage.name;

        this.filter.seller.control = new MultiSelect<IUser>();
        this.filter.seller.control.id = this.getControlId("seller");
        this.filter.seller.control.label = "Сотрудник";
        this.filter.seller.control.items = this.context?.employees.map(e => e.userRef) ?? [];
        this.filter.seller.control.textField = e => e.info.name;
    }

    private initFooter(): void {
        this.ftFooter = new Footer({
            okText: "Сохранить",
            okHandler: this.clickSave.bind(this),
            cancelHandler: this.clickCancel.bind(this),
        });
    }

    private populateFilters(): void {
        if (!this.context.currentFilter) {
            return;
        }

        const filters = this.getLocalFilter(this.context.currentFilter);

        Object.entries(filters).forEach(([k, filter]) => {
            const key = k as keyof SalesFilter;

            if (!this.filter[key]) {
                return;
            }

            this.filter[key].set(filter as any);
        });
    }

    public get controls(): Control[] {
        return Object.entries(this.filter).map(([_, filter]) => filter.control);
    }

    public get footer(): Control {
        return this.ftFooter;
    }

    // /Init

    public get tableFilter(): ITableFilter[] {
        return this.getTableFilter(this.filter);
    }

    public async clickSave(): Promise<void> {
        if (!this.onSave) {
            return;
        }

        const result = await this.onSave(this.tableFilter);

        if (result) {
            this.hide();
        }
    }

    public clickCancel(): void {
        this.hide();
    }
}
